import { useEffect, useState } from "react";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TagCreateInline from "@components/ui/tags/TagCreateInline";
import Icon1 from "@assets/icons/Icon";
import { useTagStore } from "@store/tagStore";
import { useDataStore } from "@store/user_org";
import { useArretJustificationStore } from "@store/arretJustificationStore";
import { getContrastTextColor } from "@utils/tagColor";
import TagCategoryGroups from "@components/ui/tags/TagCategoryGroups";

/**
 * « Pourquoi cet ordre est-il arrêté ? » — un tag suffit.
 * Le tag choisi devient le motif : il est ajouté aux tags de l'ordre et affiché
 * tel quel au client sur son suivi, avec la note éventuelle. Les autres tags de
 * l'ordre ne sortent pas de Carvis.
 */
export default function ArretJustificationModal() {
  const pending = useArretJustificationStore((s) => s.pending);
  if (!pending) return null;
  return <ArretJustificationDialog key={pending.tagIds.join(",")} />;
}

function ArretJustificationDialog() {
  const pending = useArretJustificationStore((s) => s.pending)!;
  const resolve = useArretJustificationStore((s) => s.resolve);
  // Le planning s'ouvre parfois sans être passé par la liste des ordres : les
  // tags n'ont alors jamais été chargés. Le fetch est sans effet s'ils le sont.
  const orgId = useDataStore((s) => s.orgData?.id);
  const fetchTags = useTagStore((s) => s.fetchTags);
  useEffect(() => { if (orgId) fetchTags(orgId); }, [orgId, fetchTags]);
  const tags = useTagStore((s) => s.tags)
    .filter((t) => t.scope === "ordre" && t.active)
    .sort((a, b) => {
      // Les tags déjà sur l'ordre d'abord : c'est souvent l'un d'eux qui explique l'arrêt.
      const ah = pending.tagIds.includes(a.id) ? 0 : 1;
      const bh = pending.tagIds.includes(b.id) ? 0 : 1;
      return ah !== bh ? ah - bh : a.name.localeCompare(b.name);
    });
  const [motifId, setMotifId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const motif = tags.find((t) => t.id === motifId) || null;

  const confirm = () => {
    if (!motif) return;
    const tag_ids = pending.tagIds.includes(motif.id) ? pending.tagIds : [...pending.tagIds, motif.id];
    resolve({
      tag_ids,
      arret_reason: {
        tag_id: motif.id,
        label: motif.name,
        note: note.trim() || undefined,
        at: new Date().toISOString(),
      },
    });
  };

  return (
    <BaseModal
      title="Motif de l'arrêt"
      startIcon={<Icon1 name="warning" size={20} />}
      onClose={() => resolve(null)}
      width="max-w-md"
    >
      <div className="flex flex-col gap-4 p-4">
        <div className="text-h3" style={{ fontFamily: "Poppins", color: "#525252" }}>
          {pending.clientName
            ? `Pourquoi l'ordre de ${pending.clientName} est-il arrêté ?`
            : "Pourquoi cet ordre est-il arrêté ?"}
          {" "}Un tag suffit : le client le verra sur son suivi, ce qui lui évite d'appeler.
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-h4 text-neutral-400">Motif (tag)</span>
          <div className="flex flex-row flex-wrap gap-1.5">
            <TagCategoryGroups tags={tags} renderTag={(tag) => {
              const selected = tag.id === motifId;
              return (
                <span
                  key={tag.id}
                  onClick={() => setMotifId(selected ? null : tag.id)}
                  className="text-h3 whitespace-nowrap cursor-pointer select-none transition-opacity"
                  style={{
                    background: selected ? tag.color : "transparent",
                    color: selected ? getContrastTextColor(tag.color) : tag.color,
                    border: `1.5px solid ${tag.color}`,
                    borderRadius: 30,
                    padding: "3px 12px",
                    lineHeight: "18px",
                    opacity: selected ? 1 : 0.75,
                  }}
                >
                  {tag.name}
                </span>
              );
            }} />
            {tags.length === 0 && (
              <span className="text-neutral-400 text-h3">Aucun tag encore : créez-en un ci-dessous.</span>
            )}
          </div>
          <TagCreateInline
            scope="ordre"
            placeholder="Autre motif : nouveau tag…"
            autoFocus={tags.length === 0}
            onCreated={(id) => setMotifId(id)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-h4 text-neutral-400">Précision pour le client (facultatif)</span>
          <textarea
            className="w-full px-3 py-2 rounded-lg text-h3"
            style={{ border: "1px solid #E5E7EB", fontFamily: "Poppins", outline: "none", color: "#374151", minHeight: 64, resize: "vertical" }}
            placeholder="ex. Pièce commandée, livraison prévue sous 10 jours."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>

      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={() => resolve(null)}>
          Annuler
        </SecondaryButton>
        <Button variant="small" label="Arrêter l'ordre" onClick={confirm} disabled={!motif} />
      </div>
    </BaseModal>
  );
}
